/**
 * hi-faq-search.js — busca e atalhos da seção "Perguntas frequentes" (hi-faq).
 *
 * <hi-faq-search>
 *   Filtra as perguntas pelo texto digitado (pergunta + resposta + palavras
 *   extras do bloco), sem diferenciar acentos e maiúsculas. Os atalhos
 *   preenchem a busca; clicar de novo no atalho ativo limpa. Títulos de grupo
 *   somem quando nenhuma pergunta do grupo aparece.
 */
const normalize = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

class HiFaqSearch extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-faq-search]'));
    this.empty = this.querySelector('[data-faq-empty]');
    this.status = this.querySelector('[data-faq-status]');
    this.chips = [...this.querySelectorAll('[data-faq-chip]')];
    this.entries = [...this.querySelectorAll('.hi-faq__item')].map((item) => ({
      item,
      text: normalize(`${item.textContent} ${item.getAttribute('data-keywords') || ''}`),
    }));

    this.input?.addEventListener('input', () => this.#filter());
    this.chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        if (!this.input) return;
        const value = chip.getAttribute('data-faq-chip') || '';
        this.input.value = normalize(this.input.value) === normalize(value) ? '' : value;
        this.#filter();
      })
    );
  }

  #filter() {
    const terms = normalize(this.input?.value).split(/\s+/).filter(Boolean);
    let shown = 0;

    for (const { item, text } of this.entries) {
      const match = terms.every((term) => text.includes(term));
      item.hidden = !match;
      if (match) shown += 1;
      // Com busca ativa, abre a única resposta encontrada.
      if (terms.length && match && shown === 1) item.open = true;
    }

    // Esconde títulos de grupo sem perguntas visíveis.
    this.querySelectorAll('.hi-faq__group').forEach((group) => {
      let next = group.nextElementSibling;
      let visible = false;
      while (next && !next.classList.contains('hi-faq__group')) {
        if (next.classList.contains('hi-faq__item') && !next.hidden) visible = true;
        next = next.nextElementSibling;
      }
      /** @type {HTMLElement} */ (group).hidden = !visible;
    });

    const query = normalize(this.input?.value);
    this.chips.forEach((chip) =>
      chip.setAttribute('aria-pressed', String(normalize(chip.getAttribute('data-faq-chip')) === query))
    );
    if (this.empty) /** @type {HTMLElement} */ (this.empty).hidden = shown > 0;
    if (this.status) this.status.textContent = terms.length ? `${shown} pergunta(s) encontrada(s)` : '';
  }
}

if (!customElements.get('hi-faq-search')) customElements.define('hi-faq-search', HiFaqSearch);
